// Blueprint: forge/blueprint/frontend/architecture/001-shell-composition.md
import { DocumentTitle } from '@servicetitan/document-title';
import { BASENAME_TOKEN, Loader } from '@servicetitan/web-components';
import { useDependencies, useOptionalDependencies } from '@servicetitan/react-ioc';
import { Fragment, memo, useMemo } from 'react';

import { LoadingSpinner } from './loading-spinner/LoadingSpinner';
import { ModuleSettingsOverrideToken, RootOrgIdToken } from '../tokens';
import type { RouteModule } from '../stores/app.store';

/*
 * DocumentTitle rewrites document.title on every update, and ModulePage re-renders on each location
 * change. Memoized so it only writes on mount or a new name, and a more specific title the child MFE
 * set (e.g. a user's name on their detail page) survives navigation inside that module.
 */
const ModuleTitle = memo(DocumentTitle);

interface ModulePageProps {
    module: RouteModule;
}

export function ModulePage({ module }: ModulePageProps) {
    const [rootOrgId, settingsOverride] = useDependencies(
        RootOrgIdToken,
        ModuleSettingsOverrideToken
    );
    const [basename] = useOptionalDependencies(BASENAME_TOKEN);
    const resolvedRootOrgId = rootOrgId ?? '';

    // Stable `data` identity — Loader re-handles mount data when the object reference changes.
    const data = useMemo(
        () => ({
            // Registry settings are per environment, not per host, so the host's values win.
            settings: { ...module.settings, ...settingsOverride },
            rootOrgId: resolvedRootOrgId,
            // TODO: deprecate aliases once child MFEs read `rootOrgId` only.
            entryUnitId: resolvedRootOrgId,
            rootNodeId: resolvedRootOrgId,
            orgHubBaseName: basename,
        }),
        [module.settings, settingsOverride, resolvedRootOrgId, basename]
    );

    const baseName = (basename ?? '') + module.route;

    return (
        <Fragment>
            <ModuleTitle title={module.displayName} />
            <Loader
                key={module.id}
                src={module.mfeUrl}
                basename={baseName}
                data={data}
                loadingFallback={<LoadingSpinner />}
                cache
            />
        </Fragment>
    );
}
